import { AlertTriangle, CheckCircle, type LucideIcon } from "lucide-react"

import { type SubscriptionStatus, SubscriptionTier } from "./types"

/**
 * Group donation subscription plans.
 * Subscriptions donate to group balance only — no personal rating bonus.
 */
export const SUBSCRIPTION_PLANS = [
  {
    tier: SubscriptionTier.TIER_1,
    name: "Lite",
    stars: 50,
    groupDonation: 100,
    supportProMessages: 20,
    aiMessages: 100,
    aiAntispamChecks: 300
  },
  {
    tier: SubscriptionTier.TIER_2,
    name: "Basic",
    stars: 100,
    groupDonation: 200,
    supportProMessages: 40,
    aiMessages: 200,
    aiAntispamChecks: 600
  },
  {
    tier: SubscriptionTier.TIER_3,
    name: "Plus",
    stars: 200,
    groupDonation: 500,
    supportProMessages: 100,
    aiMessages: 500,
    aiAntispamChecks: 1600
  },
  {
    tier: SubscriptionTier.TIER_4,
    name: "Pro",
    stars: 1000,
    groupDonation: 2500,
    supportProMessages: 500,
    aiMessages: 2500,
    aiAntispamChecks: 8300
  },
  {
    tier: SubscriptionTier.TIER_5,
    name: "Premium",
    stars: 2500,
    groupDonation: 6250,
    supportProMessages: 1250,
    aiMessages: 6250,
    aiAntispamChecks: 20800
  }
] as const

/**
 * Personal subscription plan configuration.
 * Rating goes directly to user's global personal balance (no multiplier).
 */
export const PERSONAL_SUBSCRIPTION_PLANS = [
  {
    tier: SubscriptionTier.TIER_1,
    stars: 50,
    personalRating: 100,
    name: { key: "personal-subscription-tier-1-name", fallback: "Lite" }
  },
  {
    tier: SubscriptionTier.TIER_2,
    stars: 100,
    personalRating: 200,
    name: { key: "personal-subscription-tier-2-name", fallback: "Basic" }
  },
  {
    tier: SubscriptionTier.TIER_3,
    stars: 200,
    personalRating: 500,
    name: { key: "personal-subscription-tier-3-name", fallback: "Plus" }
  },
  {
    tier: SubscriptionTier.TIER_4,
    stars: 1000,
    personalRating: 3000,
    name: { key: "personal-subscription-tier-4-name", fallback: "Pro" }
  },
  {
    tier: SubscriptionTier.TIER_5,
    stars: 2500,
    personalRating: 8250,
    name: { key: "personal-subscription-tier-5-name", fallback: "Premium" }
  }
] as const

/**
 * Subscription status → label (key + English fallback) and chip icon.
 */
export const SUBSCRIPTION_STATUS_META: Record<
  SubscriptionStatus,
  { key: string; fallback: string; icon: LucideIcon | null }
> = {
  active: { key: "subscription-status-active", fallback: "Active", icon: CheckCircle },
  pending_payment: { key: "subscription-status-pending-payment", fallback: "Pending payment", icon: AlertTriangle },
  payment_failed: { key: "subscription-status-payment-failed", fallback: "Payment failed", icon: AlertTriangle },
  cancelled: { key: "subscription-status-cancelled", fallback: "Cancelled", icon: null },
  expired: { key: "subscription-status-expired", fallback: "Expired", icon: null }
}

/**
 * A recurring RP subscription the viewer still holds: it renews, awaits its first payment, or failed a renewal.
 * The order is also the priority among a chat's plans when several are on record, most current first.
 */
export const HELD_SUBSCRIPTION_STATUSES = [
  "active",
  "pending_payment",
  "payment_failed"
] as const satisfies readonly SubscriptionStatus[]

export type HeldSubscriptionStatus = (typeof HELD_SUBSCRIPTION_STATUSES)[number]

export const isHeldSubscriptionStatus = (status: SubscriptionStatus): status is HeldSubscriptionStatus =>
  (HELD_SUBSCRIPTION_STATUSES as readonly SubscriptionStatus[]).includes(status)
